import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import api from "../../../../api/api";

export const useDeepSeek = (t, match) => {
    const [loading, setLoading] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [balance, setBalance] = useState(null);
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [schools, setSchools] = useState([]);
    
    // Selected Context
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    
    // Sidebar Filters
    const [sidebarSearch, setSidebarSearch] = useState("");
    
    // Advanced Search Filters state
    const [filters, setFilters] = useState({
        dateFrom: "",
        dateTo: "",
        schoolId: "",
        userTypeId: "",
        userName: "",
        pageSize: 100
    });

    const applyFilters = useCallback((newFilters) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters
        }));
    }, []);

    const loadUsersList = useCallback(() => {
        const params = {
            page: 1,
            pageSize: filters.pageSize,
            schoolId: filters.schoolId || undefined,
            userTypeId: filters.userTypeId || undefined,
            userName: filters.userName || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined
        };
        api.get('/DeepSeekHistory/', { params })
            .then(res => {
                setUsers(res.data.users || []);
                setSchools(res.data.schools || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [filters]);

    const loadDashboardData = useCallback(() => {
        setLoading(true);
        api.get('/DeepSeekBalance/')
            .then(res => {
                setBalance(res.data.balance);
                setStats(res.data.stats);
            })
            .catch(() => {
                toast.error(t('Error.NoConnection.Title') || "خطأ في الاتصال بالخادم");
                setLoading(false);
            });
    }, [t]);

    const fetchUserHistory = useCallback((userId) => {
        if (!userId) return;
        setLoadingHistory(true);
        setLogs([]);
        
        const params = {
            page: 1,
            pageSize: filters.pageSize,
            userId: userId,
            schoolId: filters.schoolId || undefined,
            userTypeId: filters.userTypeId || undefined,
            userName: filters.userName || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined
        };

        api.get('/DeepSeekHistory/', { params })
            .then(res => {
                setLogs(res.data.data || []);
                setLoadingHistory(false);
            })
            .catch(() => {
                toast.error(t('Error.NoConnection.Title') || "خطأ في جلب محادثات المستخدم");
                setLoadingHistory(false);
            });
    }, [t, filters]);

    const selectUser = useCallback((userObj) => {
        setSelectedUser(userObj);
        setSelectedConversationId(null);
        if (userObj) {
            fetchUserHistory(userObj.Id);
        }
    }, [fetchUserHistory]);

    const clearSelectedUser = useCallback(() => {
        setSelectedUser(null);
        setSelectedConversationId(null);
        setLogs([]);
    }, []);

    // Initial load
    useEffect(() => {
        if (typeof window !== "undefined" && window.scrollToTop) {
            window.scrollToTop();
        }
        loadDashboardData();
    }, [loadDashboardData]);

    // Handle deep linking from URL path param (match.params.id)
    useEffect(() => {
        const routeConvId = match?.params?.id;
        if (routeConvId) {
            const convId = parseInt(routeConvId) || routeConvId;
            if (convId && convId !== selectedConversationId) {
                setLoadingHistory(true);
                api.get('/DeepSeekHistory/', { params: { conversationId: convId } })
                    .then(res => {
                        const msgs = res.data.data || [];
                        if (msgs.length > 0) {
                            const firstMsg = msgs[0];
                            const userObj = {
                                Id: firstMsg.UserId,
                                Name: firstMsg.UserName,
                                Username: firstMsg.UserEmail || ""
                            };
                            setSelectedUser(userObj);
                            setSelectedConversationId(convId);
                            
                            // Now fetch the entire history for this user
                            api.get('/DeepSeekHistory/', { params: { page: 1, pageSize: filters.pageSize, userId: userObj.Id } })
                                .then(historyRes => {
                                    setLogs(historyRes.data.data || []);
                                    setLoadingHistory(false);
                                })
                                .catch(() => {
                                    setLoadingHistory(false);
                                });
                        } else {
                            setLoadingHistory(false);
                        }
                    })
                    .catch(() => {
                        setLoadingHistory(false);
                    });
            }
        }
    }, [match?.params?.id, filters.pageSize]);

    // Sync active selection to URL path parameter
    useEffect(() => {
        if (typeof window !== "undefined") {
            let newUrl = "/ControlPanel/DeepSeek";
            if (selectedConversationId) {
                newUrl = `/ControlPanel/DeepSeek/${selectedConversationId}`;
            }
            if (window.location.pathname !== newUrl) {
                window.history.replaceState({ path: newUrl }, '', newUrl);
            }
        }
    }, [selectedConversationId]);

    // Reload users list when filters change
    useEffect(() => {
        loadUsersList();
    }, [filters, loadUsersList]);

    const groupLogsByConversation = useCallback((chatLogs) => {
        if (!chatLogs || chatLogs.length === 0) return [];
        const groups = {};
        chatLogs.forEach(log => {
            const convId = log.ConversationId || `legacy_${log.Id}`;
            if (!groups[convId]) {
                groups[convId] = {
                    id: convId,
                    title: t('Pages.DeepSeekAdmin.conversation') || "محادثة",
                    dateTime: log.DateTime,
                    agent: log.Agent || "",
                    difficulty: log.Difficulty || "mid",
                    price: 0,
                    promptTokens: 0,
                    completionTokens: 0,
                    turns: []
                };
            }
            groups[convId].turns.push(log);
            groups[convId].price += log.Price || 0;
            groups[convId].promptTokens += log.PromptTokens || 0;
            groups[convId].completionTokens += log.CompletionTokens || 0;

            if (log.Agent) {
                groups[convId].agent = log.Agent;
            }
            if (log.Difficulty) {
                groups[convId].difficulty = log.Difficulty;
            }
            if (log.RecommendationTopic) {
                groups[convId].recommendationTopic = log.RecommendationTopic;
            }
            if (log.StartFromTopic !== undefined && log.StartFromTopic !== null) {
                groups[convId].startFromTopic = log.StartFromTopic;
            }
            if (new Date(log.DateTime) > new Date(groups[convId].dateTime)) {
                groups[convId].dateTime = log.DateTime;
            }
        });

        const convList = Object.values(groups);
        convList.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));

        convList.forEach(c => {
            c.turns.sort((a, b) => a.Id - b.Id);
            
            // Find the first user message in c.turns to set the title
            const firstUserTurn = c.turns.find(t => !t.IsFromBot && (t.Message || t.Prompt));
            if (firstUserTurn) {
                const firstPrompt = firstUserTurn.Message || firstUserTurn.Prompt;
                c.title = firstPrompt.length > 40 ? firstPrompt.substring(0, 40) + "..." : firstPrompt;
            } else {
                // Fallback to first log item that has any Message, Prompt or Answer
                const firstTurnWithText = c.turns.find(t => t.Message || t.Prompt || t.Answer);
                if (firstTurnWithText) {
                    const text = firstTurnWithText.Message || firstTurnWithText.Prompt || firstTurnWithText.Answer || "";
                    c.title = text.length > 40 ? text.substring(0, 40) + "..." : text;
                }
            }
        });

        return convList;
    }, [t]);

    const groupedConversations = useMemo(() => {
        return groupLogsByConversation(logs);
    }, [logs, groupLogsByConversation]);

    return {
        loading,
        loadingHistory,
        balance,
        stats,
        logs,
        groupedConversations,
        users,
        schools,
        selectedUser,
        selectedConversationId,
        setSelectedConversationId,
        sidebarSearch,
        setSidebarSearch,
        selectUser,
        clearSelectedUser,
        filters,
        applyFilters,
        refresh: loadDashboardData
    };
};
